import type { TypedDocumentNode } from '@apollo/client';
import { gql } from '@apollo/client';
import { Fragment, type ReactNode } from 'react';
import { FormattedMessage, FormattedNumber } from 'react-intl';
import { styled } from 'styled-components';

import { ScoreStatus } from '@sorare/core/src/__generated__/globalTypes';
import { ButtonBase } from '@sorare/core/src/atoms/buttons/ButtonBase';
import { Dot } from '@sorare/core/src/atoms/icons/Dot';
import { Horizontal, Vertical } from '@sorare/core/src/atoms/layout/flex';
import {
  BodyS,
  BrandXS,
  HeadlineM,
  LabelL,
  LabelS,
} from '@sorare/core/src/atoms/typography';
import { Score } from '@sorare/core/src/components/scoring/Score';
import { getPositionInitials } from '@sorare/core/src/lib/positions';

import type { TopPlayers_AnyGameInterface } from './__generated__/index.graphql.ts';

const PlayerList = styled.div`
  display: grid;
  gap: var(--unit) var(--double-unit);
  grid-template-columns: 1fr repeat(3, min-content);

  & > :first-child {
    grid-column-start: 2;
  }
`;
const PlayerAvatar = styled.img`
  width: 45px;
  height: 45px;
  object-fit: cover;
  border-radius: 50%;
  outline: 1px solid var(--c-nd-150);
`;
const TeamLogo = styled.img`
  height: var(--double-unit);
`;
const CenteredLabelS = styled(LabelS)`
  justify-self: center;
`;
const ScoreWrapper = styled.div`
  align-self: center;
  justify-self: center;
`;

type Props = {
  title: ReactNode;
  anyGame: TopPlayers_AnyGameInterface;
  players: TopPlayers_AnyGameInterface['anyPlayers'];
  onPlayerDetailsClick?: (playerSlug: string) => void;
};
export const TopPlayers = ({
  title,
  anyGame,
  players,
  onPlayerDetailsClick,
}: Props) => {
  return (
    <Vertical gap={3}>
      <HeadlineM bold>{title}</HeadlineM>
      <PlayerList>
        <CenteredLabelS uppercase>
          <FormattedMessage defaultMessage="Game" />
        </CenteredLabelS>
        <CenteredLabelS uppercase>
          <FormattedMessage defaultMessage="L10" />
        </CenteredLabelS>
        <CenteredLabelS uppercase>
          <FormattedMessage defaultMessage="Diff" />
        </CenteredLabelS>
        {players.map(player => {
          const cardAvatar = player.lastOwnedCards?.[0]?.pictureUrl;
          const averageScore = player.L10;

          return (
            <Fragment key={`${anyGame.id}-${player.slug}`}>
              <ButtonBase onClick={() => onPlayerDetailsClick?.(player.slug)}>
                <Horizontal>
                  <PlayerAvatar
                    src={cardAvatar || player.avatarPictureUrl || ''}
                  />
                  <Vertical gap={0} className="text-left">
                    <LabelL bold>{player.displayName}</LabelL>
                    <Horizontal gap={0.5}>
                      <TeamLogo src={player.activeClub?.pictureUrl || ''} />
                      <BodyS color="var(--c-nd-600)">
                        {player.activeClub?.shortName}
                      </BodyS>
                      <BodyS color="var(--c-nd-600)">
                        <Dot size={0.5} />
                      </BodyS>
                      <BodyS color="var(--c-nd-600)">
                        {player.anyPositions
                          .map(position => getPositionInitials(position))
                          .join(', ')}
                      </BodyS>
                    </Horizontal>
                  </Vertical>
                </Horizontal>
              </ButtonBase>
              <ScoreWrapper>
                <Score
                  score={player.playerGameScore?.score ?? 0}
                  scoreStatus={
                    player.playerGameScore?.scoreStatus ?? ScoreStatus.FINAL
                  }
                  shape="PILL"
                  hideReviewing
                />
              </ScoreWrapper>
              <ScoreWrapper>
                <Score
                  score={averageScore ?? 0}
                  scoreStatus={ScoreStatus.FINAL}
                  shape="HEXAGON"
                  hideReviewing
                />
              </ScoreWrapper>
              <ScoreWrapper>
                <BrandXS>
                  <FormattedNumber
                    value={
                      (player.playerGameScore?.score ?? 0) - (averageScore ?? 0)
                    }
                    signDisplay="always"
                  />
                </BrandXS>
              </ScoreWrapper>
            </Fragment>
          );
        })}
      </PlayerList>
    </Vertical>
  );
};

TopPlayers.fragments = {
  AnyGameInterface: gql`
    fragment TopPlayers_AnyGameInterface on AnyGameInterface {
      id
      anyPlayers {
        slug
        avatarPictureUrl
        anyPositions
        displayName
        L10: averageScore(type: LAST_TEN_PLAYED_SO5_AVERAGE_SCORE)
        activeClub {
          slug
          shortName
          pictureUrl
        }
        lastOwnedCards(rarities: [limited, rare, super_rare, unique]) {
          slug
          pictureUrl(derivative: "avatar")
        }
        playerGameScore(gameId: $gameIdWithoutPrefix) {
          id
          score
          scoreStatus
        }
      }
    }
  ` as TypedDocumentNode<TopPlayers_AnyGameInterface>,
};
